// home.ts — lógica de la landing: i18n ES/EN (mecanismo en i18n.ts), pricing COMPARTIDO con el portal
// (tiers.ts → /api/plans), demo REAL embebido (clic en el poster → iframe del tablero de muestra en el
// mismo lugar) y reveals suaves al scrollear (respetando prefers-reduced-motion).
import { applyI18n, detectLocale, tt } from './i18n.js';
import { loadTiers, tierColsHtml } from './tiers.js';
import { initConsent } from './consent.js';
// Diccionario EN de la landing: JSON (datos, no código) porque también lo consume scripts/gen-en.mjs para
// PRE-RENDERIZAR public/en.html en el build (SEO: el swap por JS es invisible para los crawlers). Las
// claves `__*` son metas del <head> que solo usa el generador; applyI18n las ignora (sin data-i18n).
import EN from './home.en.json' with { type: 'json' };

// ── Relay del handshake del parásito (demo embebido) ──
// El parásito (dentro del reporte de Google Data Studio) manda su handshake a `window.top` — que aquí es
// la LANDING, no la página del board del iframe intermedio. Sin relay, el board nunca recibe la telemetría
// y se queda en "preparando". La landing se pone EN MEDIO y reenvía los mensajes MOVAI_* en ambos sentidos
// (para el parásito es "el Portal"; para el board es "el parásito"). NO reimplementa lógica: solo relaya.
// Directo /b/ y el fallback móvil no pasan por aquí (ahí el board YA es window.top).
function mountDemoRelay(iframe: HTMLIFrameElement): void {
  let reportWin: Window | null = null; // ventana del parásito (descendiente profundo), capturada del READY
  let lastReady: unknown = null; // por si el board aún no montó su listener cuando llega el 1er READY
  const boardWin = (): Window | null => iframe.contentWindow;
  window.addEventListener('message', (e: MessageEvent) => {
    const d = e.data as { type?: string } | undefined;
    if (!d || typeof d !== 'object' || typeof d.type !== 'string' || !d.type.startsWith('MOVAI')) return;
    if (e.source === boardWin()) {
      // board → parásito (MOVAI_HELLO, MOVAI_COMMAND)
      reportWin?.postMessage(d, '*');
    } else {
      // parásito → board (MOVAI_READY, MOVAI_TELEMETRY)
      if (d.type === 'MOVAI_READY') {
        reportWin = e.source as Window | null;
        lastReady = d;
      }
      boardWin()?.postMessage(d, '*');
    }
  });
  // Si el board carga después del primer READY, reenvíaselo en cuanto esté listo (el parásito reintenta,
  // pero esto cierra la carrera sin depender de sus reintentos).
  iframe.addEventListener('load', () => {
    if (lastReady) boardWin()?.postMessage(lastReady, '*');
  });
}

// ── Demo real embebido: el poster (mockup CSS) se reemplaza por el iframe del tablero de muestra. En
// pantallas chicas el board embebido no cabe digno → navegar al demo completo. ──
function initDemoEmbed(): void {
  const btn = document.getElementById('ld-demo-cta');
  const url = btn?.getAttribute('data-demo');
  if (!btn || !url) return;
  btn.addEventListener('click', () => {
    if (window.matchMedia('(max-width: 640px)').matches) {
      location.href = `${url}?ref=landing`;
      return;
    }
    const stage = document.querySelector('.ld-stage');
    if (!stage) return;
    stage.classList.add('ld-stage--live');
    stage.innerHTML = `
      <div class="ld-stage-head">
        <div class="ld-stage-dots"><i></i><i></i><i></i></div>
        <span>${tt({ es: 'Demo en vivo — pregunta lo que quieras', en: 'Live demo — ask anything' })}</span>
      </div>
      <iframe class="ld-demo-frame" src="${url}?ref=landing-embed" title="MovAI Boards demo" allow="clipboard-write"></iframe>`;
    // El parásito manda su handshake a window.top (la landing): la landing relaya hacia el board del iframe.
    const frame = stage.querySelector('.ld-demo-frame') as HTMLIFrameElement | null;
    if (frame) mountDemoRelay(frame);
  });
}

// ── Pricing compartido: mismas columnas que el modal del portal, con CTA a /login. ──
async function mountTiers(): Promise<void> {
  const grid = document.getElementById('ld-tiers-grid');
  if (!grid) return;
  let cycle: 'monthly' | 'annual' = 'monthly';
  let tiers;
  try {
    tiers = await loadTiers();
  } catch {
    grid.innerHTML = `<p class="ld-tiers-error">${tt({ es: 'No se pudieron cargar los planes.', en: 'Could not load plans.' })}</p>`;
    return;
  }
  const label = (id: string): string =>
    id === 'free'
      ? tt({ es: 'Empieza gratis', en: 'Start free' })
      : tt({ es: `Elegir ${id === 'pro' ? 'Pro' : 'Premium'}`, en: `Choose ${id === 'pro' ? 'Pro' : 'Premium'}` });
  const paint = (): void => {
    grid.innerHTML = tierColsHtml(tiers!, cycle, {
      cta: (t) => `<a class="tier-cta ${t.id === 'premium' ? 'primary' : 'ghost'}" href="/login">${label(t.id)}</a>`,
    });
  };
  document.getElementById('ld-cycle')?.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest('[data-cycle]') as HTMLElement | null;
    if (!b) return;
    cycle = b.dataset.cycle as 'monthly' | 'annual';
    document.querySelectorAll('#ld-cycle [data-cycle]').forEach((x) => x.classList.toggle('active', x === b));
    paint();
  });
  paint();
}

// ── Reveals: las secciones entran con un fundido/deslizamiento sutil (nada si reduced-motion). ──
function initReveals(): void {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const targets = document.querySelectorAll('.ld-section, .ld-scene, .ld-stage-wrap');
  const io = new IntersectionObserver(
    (entries) => {
      for (const en of entries)
        if (en.isIntersecting) {
          en.target.classList.add('ld-in');
          io.unobserve(en.target);
        }
    },
    { rootMargin: '0px 0px -8% 0px' },
  );
  targets.forEach((t) => {
    t.classList.add('ld-reveal');
    io.observe(t);
  });
}

function boot(): void {
  if (detectLocale() !== 'es') applyI18n(EN);
  initConsent(); // analítica opt-in (inerte sin GA_ID)
  initDemoEmbed();
  void mountTiers();
  initReveals();
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
